/**
 * Centralized logging utility with configurable log levels
 * Replaces scattered console.log statements throughout the modal system
 */

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
  TRACE = 4,
}

export interface LoggerConfig {
  level: LogLevel;
  prefix?: string;
  enabled?: boolean;
  timestamp?: boolean;
}

class Logger {
  private config: LoggerConfig = {
    level: LogLevel.WARN,
    enabled: true,
    timestamp: false,
    prefix: '',
  };

  constructor(config?: Partial<LoggerConfig>) {
    if (config) {
      this.configure(config);
    }

    // Set from environment in development
    if (import.meta.env.DEV) {
      this.config.level = LogLevel.DEBUG;
      this.config.timestamp = true;
    }
  }

  configure(config: Partial<LoggerConfig>) {
    this.config = { ...this.config, ...config };
  }

  private shouldLog(level: LogLevel): boolean {
    return this.config.enabled !== false && level <= this.config.level;
  }

  private format(
    level: string,
    message: string,
    ...args: unknown[]
  ): unknown[] {
    const prefix = this.config.prefix ? `[${this.config.prefix}]` : '';
    const timestamp = this.config.timestamp
      ? `[${new Date().toISOString()}]`
      : '';
    const levelTag = `[${level}]`;

    return [`${timestamp}${levelTag}${prefix} ${message}`, ...args];
  }

  error(message: string, ...args: unknown[]) {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(...this.format('ERROR', message, ...args));
    }
  }

  warn(message: string, ...args: unknown[]) {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(...this.format('WARN', message, ...args));
    }
  }

  info(message: string, ...args: unknown[]) {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(...this.format('INFO', message, ...args));
    }
  }

  debug(message: string, ...args: unknown[]) {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(...this.format('DEBUG', message, ...args));
    }
  }

  trace(message: string, ...args: unknown[]) {
    if (this.shouldLog(LogLevel.TRACE)) {
      console.log(...this.format('TRACE', message, ...args));
    }
  }

  // Create a child logger with additional prefix
  child(prefix: string): Logger {
    const childPrefix = this.config.prefix
      ? `${this.config.prefix}:${prefix}`
      : prefix;

    return new Logger({
      ...this.config,
      prefix: childPrefix,
    });
  }
}

// Export singleton instance for modal system
export const modalLogger = new Logger({
  prefix: 'Modal',
  level: import.meta.env.DEV ? LogLevel.DEBUG : LogLevel.WARN,
});

// Export factory for creating component-specific loggers
export function createLogger(
  prefix: string,
  config?: Partial<LoggerConfig>
): Logger {
  return new Logger({
    prefix,
    ...config,
  });
}

// Export default logger
export default new Logger();
